import {ContractTransactionReceipt, ethers} from "ethers";
import * as gs from "@ivanlog/gs";
import { App } from "../../App";

export class ChainProvider
{
    private _provider: ethers.AbstractProvider;
    private _signer: ethers.JsonRpcSigner;
    constructor(provider: ethers.AbstractProvider, signer:ethers.JsonRpcSigner|undefined=undefined)
    {
        this._provider=provider;
        this._signer=signer;
    }

    public get provider() {return this._provider;}
    public get signer() {return this._signer;}

    public async getBalanceAsync(address:string)
    {
        return await this._provider.getBalance(address);
    }
}

export abstract class ContractFacade
{
    public static WaitBlocks = 1;
    protected _address:string;
    protected _contract: ethers.Contract;
    protected _provider:ChainProvider;
    protected _signer: ethers.JsonRpcSigner;
    protected _abi: any;

    constructor(address:string, abi:any, chainProvider:ChainProvider)
    {
        this._address=address;
        this._abi=abi;
        this.setProviderAsync(chainProvider);
    }

    public async setProviderAsync(chainProvider:ChainProvider)
    {
        this._provider=chainProvider;
        if (this._provider.signer) this._contract = new ethers.Contract(this._address,this._abi, this._provider.signer);
        else this._contract = new ethers.Contract(this._address, this._abi, this._provider.provider);
    }

    public get address(){return this._address;}
   
    protected async _call(method:string, ...args) {
        let tryCount=0;
        let skip=false;
        const processError = async (err)=>{
            tryCount++;
            if ((err as any).code === "CALL_EXCEPTION") {
                let message = (err as any).shortMessage || (err as any).reason;
                if (message=="missing revert data"&&tryCount>2)
                {
                    App.gui.dialogOkShowAsync("RPC is overloaded. Please wait 5min and try again.","");
                    return;
                }
                console.error("Revert от контракта:", message);
                console.debug("Raw data:", (err as any).data);
                if (tryCount>2)
                    App.gui.dialogOkShowAsync(gs.text.clearHtmlToText(message)+" ("+this._address+")","RPC call error");
            } else
            if ((err as any).code === "NETWORK_ERROR") { //Revert
                App.gui.dialogOkShowAsync("RPC error","Connection error");
            } else
            console.error("Неизвестная ошибка:", err);
            await this._awaitTime(tryCount*1000);
        }

        for(let i=0;i<3;i++)
        {
            try { return await this._contract[method](...args); } 
            catch (e) { await processError(e); } //skip first fail
            if (skip) return;
        }
    }

    protected async _callInSilence(method:string, ...args) {
        
        try { return await this._contract.methods[method](...args); } 
        catch (e) { }
    }

    protected async _send(method:string, ...args) {
        const fn = this._contract.getFunction(method);
        let gasLimit:BigInt;
        try { 
            let gas = await fn.estimateGas(...args);
            gasLimit = gas * 2n;
        }
        catch (e) { this._informChainError(e); return undefined; }
        try { 
            
            const tx = await fn(...args,{ gasLimit:gasLimit});
            const receipt = (await tx.wait(ContractFacade.WaitBlocks)) as ContractTransactionReceipt;
            if (receipt.status==1) return true;
            throw new Error(`Transaction ${receipt.hash} failed`);
        }
        catch (e) { this._informChainError(e); return false; }    
    }

    protected async _pay(value:string, method:string, ...args) {
        const fn = this._contract.getFunction(method);
        let gasLimit:BigInt;
        try {
            // Считаем газ
            let gas = await fn.estimateGas(...args, { value: value });
            gasLimit = gas * 2n;
        }
        catch (e) { this._informChainError(e); return undefined; }   
        try { 
            const tx = await fn(...args, { value: value, gasLimit:gasLimit }); 
            const receipt = (await tx.wait(ContractFacade.WaitBlocks)) as ContractTransactionReceipt;
            if (receipt.status==1) return true;
            throw new Error(`Transaction ${receipt.hash} failed`);
        }
        catch (e) { this._informChainError(e); return false; }    
    }

    protected _toDecimal(wei: string | bigint): string {
        const value = ethers.formatEther(wei);
        return (value === "0." || value === "0") ? "0.0" : value;
    }

    protected _toWei(ether: string): string {
        if (ether === "0." || ether === "0.0" || ether === "0") return "0";
        return ethers.parseEther(ether).toString();
    }

    protected _div(hexValue:string, div:number):string
    {
        const value = BigInt(hexValue);     
        const result = value / BigInt(div); 
        return ethers.toBeHex(result);
    }

    private _informChainError(data)
    {
        if (data.action=="estimateGas")
        {
            let message = data.reason;
            if (message) App.gui.dialogOkShowAsync(gs.text.clearHtmlToText(message), "Transaction initiation failed"); 
            else App.gui.dialogOkShowAsync("Try again", "Transaction initiation failed"); 
        } else if (data.action=="sendTransaction"&&data.reason=="rejected")
        {
            App.gui.dialogOkShowAsync("Transaction cancelled by user", "Transaction cancelled"); 
        } else
        {
            let message = data.reason;
            if (!message) message=data.message;
            if (message) App.gui.dialogOkShowAsync(gs.text.clearHtmlToText(message), "Transaction reverted"); 
            else App.gui.dialogOkShowAsync("Try again", "Transaction reverted"); 
        }
    } 

    private _awaitTime(timeMS:number):Promise<void>
    {
        return new Promise((resolve, reject)=>{
            setTimeout(()=>resolve(), timeMS);
        })
    }
}